import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { taskRepo } from '../../data/db';
import { tasksApi, extractOne } from '../../api/facades';

const TaskDetailPage: React.FC = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = taskId ? parseInt(taskId, 10) : NaN;
  const isApiTask = !Number.isNaN(numId) && String(numId) === String(taskId);
  const [showDelete, setShowDelete] = React.useState(false);

  const { data: apiTaskRes, isLoading: apiLoading } = useQuery({
    queryKey: ['tasks', numId],
    queryFn: () => tasksApi.get(numId),
    enabled: isApiTask,
  });
  const apiTask = extractOne(apiTaskRes);

  const localTask = taskId && !isApiTask ? taskRepo.get(taskId) : undefined;

  const deleteApi = useMutation({
    mutationFn: () => tasksApi.remove(numId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setShowDelete(false);
      toast.success('Task deleted.');
      navigate((apiTask as any)?.workflow_id ? `/dashboard/workflows/${(apiTask as any).workflow_id}` : '/dashboard/tasks');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const handleDeleteLocal = () => {
    if (!localTask) return;
    const ok = window.confirm('Delete this task? This cannot be undone.');
    if (!ok) return;
    taskRepo.delete(localTask.id);
    navigate('/dashboard/tasks');
  };

  if (isApiTask) {
    if (apiLoading || !apiTask) {
      return (
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <p className="text-slate-500 dark:text-slate-400">Loading…</p>
          <Button variant="secondary" className="mt-4" onClick={() => navigate(-1)}>Back</Button>
        </div>
      );
    }
    const t = apiTask as any;
    const assignees = (t.members ?? []).map((m: any) => m.user?.people?.full_name ?? m.user?.email).filter(Boolean).join(', ') || '—';

    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200">{t.task_subject}</h1>
            {t.workflow && <p className="text-slate-600 dark:text-slate-400 mt-1">Workflow: {t.workflow.name}</p>}
            <p className="text-slate-600 dark:text-slate-400 mt-1">Assignees: {assignees}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
            <Button variant="primary" onClick={() => navigate(`/dashboard/tasks/${t.id}/edit`)}>Edit</Button>
            <Button variant="secondary" onClick={() => setShowDelete(true)} className="text-red-600 hover:text-red-700">Delete</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</div>
            <div className="mt-1 text-sm text-slate-700 dark:text-slate-200">{t.project_status ?? '—'}</div>
          </div>
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Priority</div>
            <div className="mt-1 text-sm text-slate-700 dark:text-slate-200">{t.priority ?? '—'}</div>
          </div>
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Order</div>
            <div className="mt-1 text-sm text-slate-700 dark:text-slate-200">{t.order ?? '—'}</div>
          </div>
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Depends on</div>
            <div className="mt-1 text-sm text-slate-700 dark:text-slate-200">{t.depends_on_task_id ? `Task #${t.depends_on_task_id}` : '—'}</div>
          </div>
          {t.start_date && (
            <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Start date</div>
              <div className="mt-1 text-sm text-slate-700 dark:text-slate-200">{String(t.start_date).slice(0, 10)}</div>
            </div>
          )}
          {t.end_date && (
            <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">End date</div>
              <div className="mt-1 text-sm text-slate-700 dark:text-slate-200">{String(t.end_date).slice(0, 10)}</div>
            </div>
          )}
        </div>
        {t.description && (
          <div className="mt-4 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</div>
            <div className="mt-1 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{t.description}</div>
          </div>
        )}

        <ConfirmDeleteModal open={showDelete} onClose={() => setShowDelete(false)} onConfirm={() => deleteApi.mutate()} title="Delete task" message="Are you sure? This cannot be undone." loading={deleteApi.isPending} />
      </div>
    );
  }

  if (!taskId || !localTask) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200 mb-2">Task not found</h1>
        <Button variant="primary" onClick={() => navigate('/dashboard/tasks')}>Back to Tasks</Button>
      </div>
    );
  }

  const item = localTask;
  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200">{item.title}</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Assigned: {item.assignedTo?.username ?? 'Unassigned'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate(`/dashboard/tasks/${item.id}/edit`)}>Edit</Button>
          <Button variant="secondary" onClick={handleDeleteLocal} className="text-red-600 hover:text-red-700">Delete</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Due date</div>
          <div className="mt-1 text-sm text-slate-700 dark:text-slate-200">{new Date(item.dueDate).toLocaleString()}</div>
        </div>
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</div>
          <div className="mt-1">
            <span
              className="px-2.5 py-1 text-xs font-bold leading-tight text-center align-baseline uppercase rounded-full"
              style={{ backgroundColor: (item.status?.color ?? '#94a3b8') + '20', color: item.status?.color ?? '#64748b' }}
            >
              {item.status?.label ?? '—'}
            </span>
          </div>
        </div>
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Priority</div>
          <div className="mt-1 text-sm text-slate-700 dark:text-slate-200 uppercase">{item.priority ?? '—'}</div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailPage;
